import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  useColumnOrder,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'
import { Grid, makeStyles } from '@material-ui/core'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import { ReactComponent as PersonAddAltIcon } from 'civic-champs-shared/core/icon/icons/person-add-alt.svg'
import { ReactComponent as PersonRemoveIcon } from 'civic-champs-shared/core/icon/icons/person-remove.svg'
import { map } from 'lodash'
import { RouteComponentProps } from 'react-router'
import { useTimeZone } from 'civic-champs-shared/auth/hooks'

import Loading from 'components/Loading'
import { ExtendedPagedTable } from 'core/table/components/ExtendedPagedTable'
import { TableMenuButton } from 'civic-champs-shared/core/TableMenuButton'
import TablePopoverButton from 'civic-champs-shared/core/TablePopoverButton'
import { buildTableFilters, useGetGlobalFilter } from 'core/table/filters'
import CUSTOM_FILTERS from 'champion/utils/filters'
import {
  ARRAY_OPERATOR_OPTIONS,
  DATE_OPERATOR_OPTIONS,
  NUMBER_OPERATOR_OPTIONS,
  operatorTypes,
  STRING_OPERATOR_OPTIONS,
} from 'core/table/interfaces/Filters'
import useGetColumnState from 'core/table/table-hooks/useGetColumnState'
import usePinnedTableColumn from 'core/table/table-hooks/usePinnedTableColumn'
import { useConditionalSelectColumn } from 'core/table/table-hooks/useSelectColumn'
import useGroupMemberColumns, {
  DEFAULT_GROUP_MEMBER_HIDDEN_COLUMNS,
  useGroupMemberColumnStyles,
} from 'group/hooks/useGroupMemberColumns'
import useMessageRecipientsPrompt from 'messages/hooks/useMessageRecipientsPrompt'
import ExportGroupMembersMenuButton from 'group/components/ExportGroupMembersMenuButton'
import useApproveGroupMembers from 'group/hooks/useApproveGroupMembers'
import useResendGroupInvitations from 'group/hooks/useResendGroupInvitations'
import { MessageSourceType } from 'messages/types'

const GROUP_MEMBER_DATE_FILTER_COLUMNS = new Set(['latestActivity', 'invitedOn', 'memberSince', 'birthDate'])

const tableName = 'group-members'
const ACTIONS_COLUMN_ID = 'actions'

const useGroupMembersTabStyles = makeStyles({
  popover: {
    maxWidth: '265px',
  },
  popoverButton: {
    width: '250px',
    whiteSpace: 'nowrap',
  },
  popoverActionIcon: {
    color: '#0F5DB5',
    width: 18,
    height: 18,
    flexShrink: 0,
  },
  popoverSecondaryLabel: {
    color: '#747778',
  },
})

const getOperatorOptions = (column: string) => {
  if (GROUP_MEMBER_DATE_FILTER_COLUMNS.has(column)) {
    return DATE_OPERATOR_OPTIONS
  }
  switch (column) {
    case 'firstName':
    case 'lastName':
    case 'email':
    case 'mobile':
    case 'name':
    case 'homePhoneNumber':
      return STRING_OPERATOR_OPTIONS
    case 'hours':
    case 'donations':
      return NUMBER_OPERATOR_OPTIONS
    case 'groups':
    case 'tags':
      return ARRAY_OPERATOR_OPTIONS
    case 'status':
      return [
        { value: operatorTypes.ANY_OF, displayValue: 'Includes' },
        { value: operatorTypes.NOT_ANY_OF, displayValue: 'Does not include' },
      ]
    default:
      return []
  }
}

interface GroupMembersTabProps extends RouteComponentProps {
  group: { id: number }
  members: any[]
  removeMember: (members: any[]) => void
  refresh: () => void
}

export default function GroupMembersTab({
  group,
  members,
  removeMember,
  refresh,
  history,
  location,
}: GroupMembersTabProps) {
  const skipReset = useRef<boolean>()
  const classes = useGroupMembersTabStyles()
  const columnStyles = useGroupMemberColumnStyles()
  const { columns, columnFiltersLoading } = useGroupMemberColumns(group, refresh)
  const [fetchColumnState, { loading: columnStateLoading, called: columnStateCalled, result: columnState }] =
    useGetColumnState()
  const showMessageRecipientsPrompt = useMessageRecipientsPrompt()
  const { approveMembers, loading: approveLoading } = useApproveGroupMembers(refresh)
  const { resendInvitations, loading: resendLoading } = useResendGroupInvitations(refresh)
  const timeZone = useTimeZone()
  const filterTypes = useMemo(() => ({ ...buildTableFilters(timeZone), ...CUSTOM_FILTERS }), [timeZone])

  const globalFilter = useGetGlobalFilter({
    columns,
    filterTypes,
    getOperatorOptions,
  })

  useEffect(() => {
    fetchColumnState(tableName)
  }, [fetchColumnState])

  const table = useTable(
    {
      initialState: {
        hiddenColumns: DEFAULT_GROUP_MEMBER_HIDDEN_COLUMNS,
      },
      data: members,
      columns: columns as any,
      // @ts-ignore
      globalFilter,
      filterTypes,
      autoResetSortBy: !skipReset.current,
      autoResetPage: !skipReset.current,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    useColumnOrder,
    useConditionalSelectColumn(() => true, columnStyles.selectColumn),
  )

  const selectedRows = table.selectedFlatRows
  const selectedRowsCount = selectedRows.length

  const getSearchableContacts = useCallback(
    () =>
      Promise.resolve(
        map(members, ({ person: { id, givenName, familyName, email, phoneNumber } }) => ({
          id,
          firstName: givenName,
          lastName: familyName,
          email,
          phoneNumber,
        })),
      ),
    [members],
  )

  const filterTableMembers = useCallback(() => {
    return members.filter((member, index) => table.state.selectedRowIds[index])
  }, [members, table.state.selectedRowIds])

  const selectedMembers = filterTableMembers()
  const selectedApplicantCount = selectedMembers.filter(member => member.status?.code === 'applicant').length
  const bulkActionLoading = approveLoading || resendLoading

  const columnIds = useMemo(() => columns.map((column: { id: string }) => column.id), [columns])

  usePinnedTableColumn({
    table,
    columnId: ACTIONS_COLUMN_ID,
    columnState,
    columnStateLoading,
    columnIds,
  })

  const onMessageMembers = () => {
    const recipientPersonIds = filterTableMembers().map(member => member.person.id)
    showMessageRecipientsPrompt({
      getSearchableContacts,
      recipientPersonIds,
      sourceType: MessageSourceType.Group,
      sourceId: group.id,
    })
  }

  const onRemoveMember = () => {
    removeMember(filterTableMembers())
    table.toggleAllRowsSelected(false)
  }

  const onApproveMembers = async () => {
    await approveMembers(group.id, filterTableMembers())
    table.toggleAllRowsSelected(false)
  }

  const onResendInvitations = async () => {
    const personIds = filterTableMembers().map(member => member.person.id)
    await resendInvitations(group.id, personIds)
    table.toggleAllRowsSelected(false)
  }

  if (columnStateLoading || !columnStateCalled || columnFiltersLoading) {
    return <Loading />
  }

  return (
    <Grid container>
      <ExtendedPagedTable
        history={history}
        location={location}
        table={table}
        columns={columns}
        columnState={columnState}
        tableName={tableName}
        searchPlaceholder="Search Members"
        filterSubHeader="Select member attributes to filter by:"
        getOperatorOptions={getOperatorOptions}
        buttons={
          <>
            <TableMenuButton
              startIcon={<MailOutlineIcon />}
              onClick={onMessageMembers}
              disabled={!selectedRowsCount}
              rightMargin
            >
              Message
            </TableMenuButton>
            <ExportGroupMembersMenuButton data={selectedRows} disabled={!selectedRowsCount} />
          </>
        }
        popoverClassName={classes.popover}
        popover={
          <>
            <TablePopoverButton
              className={classes.popoverButton}
              startIcon={<PersonAddAltIcon className={classes.popoverActionIcon} />}
              disabled={!selectedApplicantCount || bulkActionLoading}
              onClick={onApproveMembers}
            >
              Approve{'\u00a0'}
              <span className={classes.popoverSecondaryLabel}>(without review)</span>
            </TablePopoverButton>
            <TablePopoverButton
              className={classes.popoverButton}
              startIcon={<PersonRemoveIcon className={classes.popoverActionIcon} />}
              disabled={!selectedRowsCount || bulkActionLoading}
              onClick={onRemoveMember}
            >
              Remove{'\u00a0'}
              <span className={classes.popoverSecondaryLabel}>(without review)</span>
            </TablePopoverButton>
            <TablePopoverButton
              className={classes.popoverButton}
              startIcon={<MailOutlineIcon className={classes.popoverActionIcon} />}
              disabled={!selectedRowsCount || bulkActionLoading}
              onClick={onResendInvitations}
            >
              Resend Group Invitation
            </TablePopoverButton>
          </>
        }
        useGlobalSearch
        useFilters
        skipReset={skipReset}
      />
    </Grid>
  )
}
